import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Typography,
	styled,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Close from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import { FormType } from '@/api/enum/formType.enum';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import { useFileUploader } from '@/hooks/useFileUploader';
import { FileInfo } from '@/api/DTO/common.interface';
import {
	useCreateClockMutation,
	useUpdateClockMutation,
} from '@/api/app-setting/clockApiSlice';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
	ClickPageDTO,
	ClockRequest,
} from '@/api/DTO/app-setting/clock.interface';

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

interface Props {
	type: FormType;
	item?: ClickPageDTO;
}

export default function SetClockDialog(props: Props) {
	const { type, item } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const showModal = useModal();
	const { uploadFile } = useFileUploader();
	const [fileObj, setFileObj] = useState<FileInfo | null>(item?.media || null);
	const [createClock] = useCreateClockMutation();
	const [updateClock] = useUpdateClockMutation();
	const defaultValues: DefaultValues<ClockRequest> = {};

	useEffect(() => {
		if (item) {
			setFileObj(item?.media);
			methods.setValue('file', item?.fileName);
		}
	}, [item]);

	const formSchema = Yup.object().shape({
		file: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<ClockRequest>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setFileObj(null);
		setOpen(false);
	};

	const onSubmit: SubmitHandler<ClockRequest> = async (data) => {
		const obj: ClockRequest = {
			fileName: data.file,
			media: fileObj,
		};
		if (item?.id) obj.id = item.id;

		const response =
			type == FormType.create ? await createClock(obj) : await updateClock(obj);
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.clockCreateSuccess')
					: t('snackbar.clockUpdateSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		const validTypes = ['audio/mpeg', 'audio/aac'];
		const fileType = selectedFile?.type;
		if (fileType && !validTypes.includes(fileType)) {
			enqueueSnackbar(t('appSetting.canMP3AAC'), {
				variant: 'warning',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			return;
		}
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			setFileObj(res);
			methods.setValue('file', res.name);
			methods.clearErrors(`file`);
		}
	};

	return (
		<>
			{type == FormType.create && (
				<Button
					variant='contained'
					endIcon={<AddCircleOutlineOutlinedIcon />}
					onClick={() => setOpen(true)}
					style={{ alignItems: 'center' }}
				>
					{t('button.addClock')}
				</Button>
			)}
			{type == FormType.edit && (
				<IconButton color='warning' onClick={() => setOpen(true)}>
					<ModeOutlinedIcon />
				</IconButton>
			)}

			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{type == FormType.create
						? t('appSetting.addClock')
						: t('appSetting.replaceVideo')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppTextField
										disabled
										control={methods.control}
										name='file'
										label={t('appSetting.uploadVideo')}
										InputProps={{
											endAdornment: (
												<InputAdornment position='end'>
													<IconButton component='label'>
														<DriveFolderUploadIcon />
														<VisuallyHiddenInput
															type='file'
															accept='.mp3,.aac'
															onChange={handleUpload}
															// ref={imageInputRef}
														/>
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
									<Typography variant='caption'>
										{t('appSetting.canMP3AAC')}
									</Typography>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{type == FormType.create ? t('button.create') : t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
